import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { Cache } from './cache.service';
import {
  CreateRangeRequest,
  CreateRangeResponse,
  GetRangeRequest,
  GetRangeResponse,
  ListRangesRequest,
  ListRangesResponse,
  SituViewWebApiClient,
  UpdateRangeRequest,
  UpdateRangeResponse,
  UpdateProductRangeRequest,
  UpdateProductRangeResponse,
  UpdateProductRangeAuthorisedCompanyRequest,
  UpdateProductRangeAuthorisedCompanyResponse,
  Range
} from 'situview-webapi-client';
import { ICache } from './theme.service';

@Injectable({
  providedIn: 'root'
})
export class RangeService {
  cacheKey: Guid = Guid.create();
  oldCacheKey: Guid = Guid.create();
  private fiveMinuteCacheDuration: number = 5 * 60 * 1000;
  private twoMinuteCacheDuration: number = 2 * 60 * 1000;

  constructor(
    private situViewClient: SituViewWebApiClient
  ) {
    this.oldCacheKey = this.cacheKey;
  }

  ListRanges(request: ListRangesRequest, refreshData: boolean = false): Promise<ListRangesResponse> {
    if (refreshData) {
      this.RefreshData();
      return this.listRangesInternal(request, this.cacheKey, this.oldCacheKey);
    } else {
      return this.listRangesInternal(request, this.cacheKey, this.oldCacheKey);
    }
  }

  private rangesCache: ICache<Range> | undefined;
  private loadingRangesPromise: Promise<Range[]> | null = null;
  ListSharedRanges(companyId: string, refreshData: boolean = false): Promise<Range[]> {
    let request = new ListRangesRequest();
    request.companyId = companyId;
    request.pageNumber = 1;
    request.pageSize = 200;
    request.onlySharedRanges = true;
    
    if (this.rangesCache && this.rangesCache.companyId === companyId && !refreshData && (Date.now() - this.rangesCache.timestamp) < this.twoMinuteCacheDuration) {
      return Promise.resolve(this.rangesCache.data);
    } 

    if (this.loadingRangesPromise && companyId === this.rangesCache?.companyId) {
      return this.loadingRangesPromise;
    }

    this.loadingRangesPromise = this.situViewClient.listRanges(request)
      .then((response: ListRangesResponse) => {
        if (response.success && response.items) {
          this.rangesCache = {
            companyId: companyId,
            timestamp: Date.now(),
            data: response.items
          };
          return response.items;
        } else {
          throw new Error("Failed to retrieve ranges.");
        }
      })
      .catch((error) => {
        console.error(error);
        throw error; // Rethrow error to be handled by the caller
      })
      .finally(() => {
        this.loadingRangesPromise = null;
      });

    return this.loadingRangesPromise;
  }

  GetRange(request: GetRangeRequest): Promise<GetRangeResponse> {
    return this.situViewClient.getRange(request);
  }

  CreateRange(request: CreateRangeRequest): Promise<CreateRangeResponse> {
    return this.situViewClient.createRange(request)
      .then((response: CreateRangeResponse) => {
        if (response.success) {
          this.RefreshData();
        }
        return response;
      });
  }

  UpdateRange(request: UpdateRangeRequest): Promise<UpdateRangeResponse> {
    return this.situViewClient.updateRange(request)
    .then((response: UpdateRangeResponse) => {
      if (response.success) {
        this.RefreshData();
      }
      return response;
    });
  }

  UpdateProductRange(request: UpdateProductRangeRequest): Promise<UpdateProductRangeResponse> {
    return this.situViewClient.updateProductRange(request)
    .then((response: UpdateProductRangeResponse) => {
      if (response.success) {
        this.RefreshData();
      }
      return response;
    });
  }
  
  UpdateProductRangeAuthorisedCompany(request: UpdateProductRangeAuthorisedCompanyRequest): Promise<UpdateProductRangeAuthorisedCompanyResponse> {
    return this.situViewClient.updateProductRangeAuthorisedCompany(request)
    .then((response: UpdateProductRangeAuthorisedCompanyResponse) => {
      if (response.success) {
        this.RefreshData();
      }
      return response;
    });
  }
  
  RefreshData() {
    this.oldCacheKey = this.cacheKey;
    this.cacheKey = Guid.create();
  }

  @Cache({
    duration: 900000
  })
  private listRangesInternal(request: ListRangesRequest, cacheKey: Guid, oldCacheKey: Guid): Promise<ListRangesResponse> {
    return this.situViewClient.listRanges(request);
  }
}